import { useEffect, useMemo, useRef, useState } from "react";
import { Navigate, useParams } from "react-router-dom";

import MilfshakesLogo from "@/assets/all-icons/MilfshakesLogo.png";
import config from "@/config";
// import useCache from "@/hooks/useCache"; // no longer needed
import useGetSearchParams from "@/hooks/useGetSearchParams";
import useNavigateToArtwork from "@/hooks/useNavigateToArtwork";
import useProtectActionUnregistered from "@/hooks/useProtectActionUnregistered";
import useQuery from "@/hooks/useQuery";
import AuctionTimeLeftIndicator from "@/pages/artwork/content-section/components/AuctionTimeLeftIndicator";
import UserProfileImage from "@/shared-components/cards/components/UserProfileImage";
import { DropdownSort } from '@/shared-components/dropdown';
import VirtualList from "@/shared-components/home/collection-page/components/VirtualList";
import HomeHeader from "@/shared-components/home/components/HomeHeader";
import Loader from "@/shared-components/loader/Loader";
import AuctionUnregisteredPopup from "@/shared-components/popups/unregistered-user-popup/auction-unregistered-popup";
import StaticSeoHead from "@/shared-components/seo/StaticSeoHead";
import Text from "@/shared-components/text/components/Text";
import ToggleModeDarkLight from "@/shared-components/toggles/ToggleModeDarkLight";
import AppNavigationPage from "@/shared-components/wrappers/components/AppNavigationPage";
import { getArtworkArtistDisplayName } from "@/utils/artworkArtists";
import NumberUtils from "@/utils/number.utils";

import "@/shared-components/home/collection-page/index.css";
import "./index.css";

const AUCTION_EVENT_CONFIG = {
  milfshakes: {
    username: "milfshakes",
    heading: "MilfShakes Auction",
    metaDescription:
      "Discover the artworks featured in Suarte's  auction curated with creations by MilfShakes.",
  },
  "all-artwork-type": {
    heading: "All Artwork Types Auction",
    metaDescription:
      "Explore every artwork type available in Suarte’s signature All Artwork Types Auction.",
    forceAuctionPopup: true,
  },
  "all-artwork-types": {
    heading: "All Artwork Types Auction",
    metaDescription:
      "Explore every artwork type available in Suarte’s signature All Artwork Types Auction.",
    forceAuctionPopup: true,
  },
  "all-artworks": {
    heading: "All Artwork Types Auction",
    metaDescription:
      "Explore every artwork type available in Suarte’s signature All Artwork Types Auction.",
    forceAuctionPopup: true,
  },
};

const DIRECT_ENTRY_POPUP_SLUGS = new Set(["all-artwork-type", "all-artwork-types", "all-artworks"]);

function resolveDisplayName(userData, eventConfig, eventSlug) {
  if (eventConfig?.heading) return eventConfig.heading;

  const userProvided =
    userData?.user_profile_name ??
    userData?.user_name ??
    userData?.user_username ??
    eventConfig?.username;

  if (userProvided && userProvided.trim().length > 0) return userProvided;

  if (eventSlug && eventSlug.length > 0) {
    return eventSlug
      .split("-")
      .map((chunk) => chunk.charAt(0).toUpperCase() + chunk.slice(1))
      .join(" ");
  }

  return "Auction";
}

function formatPriceWithCurrency(value, currency) {
  if (value === null || value === undefined) {
    return null;
  }

  const numericValue = Number(value);
  if (Number.isFinite(numericValue)) {
    return NumberUtils.getArtworkPrice(numericValue, currency);
  }

  if (typeof value === "string") {
    const trimmed = value.trim();
    if (trimmed.length > 0) {
      return `${trimmed} ${currency}`;
    }
  }

  return null;
}

function pickFirstPresentValue(...values) {
  for (const candidate of values) {
    if (candidate === null || candidate === undefined) {
      continue;
    }

    if (typeof candidate === "string" && candidate.trim().length === 0) {
      continue;
    }

    return candidate;
  }

  return null;
}

export default function AuctionCollectionPage() {
  const { eventSlug } = useParams();
  const eventConfig = AUCTION_EVENT_CONFIG[eventSlug] ?? null;

  const [isDirectAuctionEntry, setIsDirectAuctionEntry] = useState(false);
  const [params, setParams] = useGetSearchParams({ validParams: ["sort", "q"] });
  const [query, setQuery] = useState(() => ({
    sort: params?.sort ?? "Relevant",
    q: params?.q ?? "",
    // backend supports ?limit=; keep stable to avoid excessive refetch
    limit: 50,
  }));
  const [activeCarouselIndex, setActiveCarouselIndex] = useState(0);
  const [isTouchViewport, setIsTouchViewport] = useState(false);

  // keep sort/q in sync with URL
  useEffect(() => {
    const nextSort = params?.sort ?? "Relevant";
    const nextQuery = params?.q ?? "";

    setQuery((prev) => {
      if (prev.sort === nextSort && prev.q === nextQuery) return prev;
      return { ...prev, sort: nextSort, q: nextQuery };
    });
  }, [params?.sort, params?.q]);

  // ✅ use the Node/Express route that binds getAuctionedArtworksById
  const artworksUrl = `${config.apis.api.url}/artwork/auctioned/batch/`;

  const isMilfshakes = eventSlug === "milfshakes";

  const { loading: artworksLoading, queryData, loadMoreData } = useQuery(
    `auction_${eventSlug}_batch`,
    artworksUrl,
    // controller currently only uses ?limit=; we still pass q/sort for future-proofing
    { limit: query.limit, q: query.q, sort: query.sort },
    {
      customQueryValidation: () => true, // no dependency on userId
      customKeyGetter: {},
      invalidateWhen: ["EDIT_ARTWORK", "DELETE_ARTWORK", "BUY_ARTWORK"],
    }
  );

  // backend shape: { user_id, count, data }
  const artworks = queryData?.data?.data ?? [];
  const totalDocs = queryData?.data?.totalDocs ?? 0;

  const displayName = useMemo(
    () => resolveDisplayName(null, eventConfig, eventSlug),
    [eventConfig, eventSlug]
  );

  const carouselArtworks = useMemo(
    () =>
      artworks
        .filter((item) => item?.artwork_media?.artwork_main_picture?.image_id)
        .slice(0, 4),
    [artworks]
  );

  useEffect(() => {
    if (typeof window === "undefined") {
      return undefined;
    }
    const handleResize = () => {
      setIsTouchViewport(window.innerWidth <= 750);
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const pageTitle = `${displayName} | Suarte`;
  const metaDescription =
    eventConfig?.metaDescription ??
    `Browse the latest auction artworks in this Suarte auction.`;

  const listEndpoint = `/artwork/auctioned/batch/`;
  const navigateToArtwork = useNavigateToArtwork(
    `auction_${eventSlug}_batch`,
    queryData?.data,
    queryData?.queryString ? `${listEndpoint}${queryData.queryString}` : listEndpoint,
    { type: "auction-page", label: displayName }
  );

  function handleSort(option) {
    setQuery((prev) => {
      if (prev.sort === option) return prev;
      const nextState = { ...prev, sort: option };
      setParams({ sort: option, q: nextState.q }, { replace: true });
      return nextState;
    });
  }

  function handleSearch(value) {
    setQuery((prev) => {
      if (prev.q === value) return prev;
      const nextState = { ...prev, q: value };
      setParams({ sort: nextState.sort, q: value }, { replace: true });
      return nextState;
    });
  }

  const shouldShowCarousel = carouselArtworks.length > 0;
  const hasCarouselControls = carouselArtworks.length > 1;
  const carouselTouchRef = useRef({ startX: 0, startY: 0, isTracking: false });
  const carouselWheelTimeoutRef = useRef(null);

  const handleCarouselPrev = () => {
    if (!hasCarouselControls) {
      return;
    }
    setActiveCarouselIndex((prev) => (prev - 1 + carouselArtworks.length) % carouselArtworks.length);
  };

  const handleCarouselNext = () => {
    if (!hasCarouselControls) {
      return;
    }
    setActiveCarouselIndex((prev) => (prev + 1) % carouselArtworks.length);
  };

  useEffect(() => {
    return () => {
      if (carouselWheelTimeoutRef.current) {
        clearTimeout(carouselWheelTimeoutRef.current);
        carouselWheelTimeoutRef.current = null;
      }
    };
  }, []);

  const resetCarouselTouchTracking = () => {
    carouselTouchRef.current = { startX: 0, startY: 0, isTracking: false };
  };

  const handleCarouselTouchStart = (event) => {
    if (!isTouchViewport || !hasCarouselControls) {
      return;
    }
    const touch = event.touches?.[0];
    if (!touch) {
      return;
    }
    carouselTouchRef.current = {
      startX: touch.clientX,
      startY: touch.clientY,
      isTracking: true,
    };
  };

  const handleCarouselTouchMove = (event) => {
    if (!isTouchViewport) {
      return;
    }
    const state = carouselTouchRef.current;
    if (!state.isTracking) {
      return;
    }
    const touch = event.touches?.[0];
    if (!touch) {
      return;
    }
    const deltaX = Math.abs(touch.clientX - state.startX);
    const deltaY = Math.abs(touch.clientY - state.startY);
    if (deltaY > deltaX && deltaY > 8) {
      carouselTouchRef.current = { ...state, isTracking: false };
    }
  };

  const handleCarouselTouchEnd = (event) => {
    if (!isTouchViewport) {
      resetCarouselTouchTracking();
      return;
    }
    const state = carouselTouchRef.current;
    if (!state.isTracking) {
      resetCarouselTouchTracking();
      return;
    }
    const touch = event.changedTouches?.[0];
    resetCarouselTouchTracking();
    if (!touch) {
      return;
    }
    const deltaX = touch.clientX - state.startX;
    const deltaY = touch.clientY - state.startY;
    if (Math.abs(deltaX) < 40 || Math.abs(deltaX) < Math.abs(deltaY)) {
      return;
    }
    if (deltaX > 0) {
      handleCarouselPrev();
    } else {
      handleCarouselNext();
    }
  };

  const handleCarouselWheel = (event) => {
    if (!hasCarouselControls) {
      return;
    }
    const deltaX = event.deltaX ?? 0;
    const deltaY = event.deltaY ?? 0;
    if (Math.abs(deltaX) <= Math.abs(deltaY) || Math.abs(deltaX) < 8) {
      return;
    }
    if (carouselWheelTimeoutRef.current) {
      return;
    }
    carouselWheelTimeoutRef.current = setTimeout(() => {
      carouselWheelTimeoutRef.current = null;
    }, 320);
    if (deltaX > 0) {
      handleCarouselNext();
    } else {
      handleCarouselPrev();
    }
  };

  const resolveArtworkSlug = (artwork) => artwork?.slug ?? artwork?.artwork_slug ?? null;

  const handleCarouselCardClick = (artwork) => {
    const slug = resolveArtworkSlug(artwork);
    if (slug) {
      navigateToArtwork(slug);
    }
  };

  const handleCarouselCardKeyDown = (event, artwork) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      handleCarouselCardClick(artwork);
    }
  };

  const resolveCarouselCardState = (index) => {
    const total = carouselArtworks.length;
    if (total === 0) {
      return "";
    }
    if (total === 1) {
      return index === 0 ? "active" : "";
    }
    const prevIndex = (activeCarouselIndex - 1 + total) % total;
    const nextIndex = (activeCarouselIndex + 1) % total;
    if (index === activeCarouselIndex) return "active";
    if (index === prevIndex) return "prev";
    if (index === nextIndex) return "next";
    const relativePosition = (index - activeCarouselIndex + total) % total;
    return relativePosition < total / 2 ? "hidden-right" : "hidden-left";
  };

  const buildCarouselAlt = (artwork) => {
    const title = artwork?.artwork_about?.artwork_title;
    const resolvedArtist = getArtworkArtistDisplayName(artwork);
    const artist = resolvedArtist !== "-" ? resolvedArtist : "Suarte artist";
    if (title) {
      return `${title} by ${artist}`;
    }
    return `Auction artwork by ${artist}`;
  };

  const activeCarouselTitle = useMemo(() => {
    if (!carouselArtworks.length) {
      return null;
    }
    const title = carouselArtworks[activeCarouselIndex]?.artwork_about?.artwork_title;
    if (typeof title === "string" && title.trim().length > 0) {
      return title.trim();
    }
    return "Untitled artwork";
  }, [activeCarouselIndex, carouselArtworks]);

  const activeCarouselMeta = useMemo(() => {
    const artwork = carouselArtworks[activeCarouselIndex];
    if (!artwork) {
      return { hasAuction: false };
    }

    const about = artwork.artwork_about ?? {};
    if (!about.artwork_is_auction) {
      return { hasAuction: false };
    }

    const auction = artwork.artwork_auction ?? {};
    const currency = about.artwork_currency ?? auction.currency ?? "EUR";

    const currentBidRaw = pickFirstPresentValue(
      auction.current_bid_price,
      auction.current_bid_amount,
      auction.highest_bid_amount,
      auction.top_bid_amount,
      about.final_bid_price,
    );
    const formattedCurrentBid = formatPriceWithCurrency(currentBidRaw, currency);
    const startingBidFormatted = formatPriceWithCurrency(about.price_starting_at, currency);

    const winnerSource =
      auction.current_bid ??
      (Array.isArray(auction.bids) && auction.bids.length > 0 ? auction.bids[0] : null);
    const normalizedWinner = winnerSource?.bid ?? winnerSource ?? null;

    const winnerName = normalizedWinner
      ? (
        normalizedWinner.bidder_username ??
          normalizedWinner.bidderUsername ??
          normalizedWinner.bidder?.user_username ??
          normalizedWinner.bidder?.username ??
          normalizedWinner.bidder?.name ??
          null
      )
      : null;

    const winnerType = normalizedWinner
      ? (
        normalizedWinner.bidder_type ??
          normalizedWinner.bidderType ??
          normalizedWinner.bidder?.user_type ??
          normalizedWinner.bidder?.type ??
          null
      )
      : null;

    const winnerImage = normalizedWinner
      ? (
        normalizedWinner.bidder_image_id ??
          normalizedWinner.bidderImageId ??
          normalizedWinner.bidder?.user_image?.image_id ??
          (typeof normalizedWinner.bidder_image === "string" ? normalizedWinner.bidder_image : null) ??
          normalizedWinner.bidder?.image_id ??
          normalizedWinner.bidder?.gallery_image?.image_id ??
          normalizedWinner.bidder?.artist_image?.image_id ??
          null
      )
      : null;

    const winnerAmountRaw = pickFirstPresentValue(
      normalizedWinner?.amount,
      auction.highest_bid_amount,
      currentBidRaw,
      about.final_bid_price,
    );
    const formattedWinnerAmount = formatPriceWithCurrency(winnerAmountRaw, currency);
    const hasWinner = Boolean(normalizedWinner && (winnerName || formattedWinnerAmount));

    return {
      hasAuction: true,
      currentBidLabel: formattedCurrentBid ?? startingBidFormatted ?? null,
      currentBidIsLive: Boolean(formattedCurrentBid),
      startingBidLabel: startingBidFormatted ?? null,
      hasBids: Boolean(normalizedWinner),
      winner: hasWinner
        ? {
          name: winnerName || "Anonymous collector",
          amount: formattedWinnerAmount ?? formattedCurrentBid ?? startingBidFormatted ?? null,
          type: winnerType || null,
          image: winnerImage || null,
        }
        : null,
    };
  }, [activeCarouselIndex, carouselArtworks]);

  const activeCarouselEndsAt = useMemo(() => {
    const artwork = carouselArtworks[activeCarouselIndex];
    if (!artwork?.artwork_about?.artwork_is_auction) {
      return null;
    }
    return artwork?.artwork_auction?.ends_at ?? null;
  }, [activeCarouselIndex, carouselArtworks]);

  useEffect(() => {
    if (typeof window === "undefined" || typeof document === "undefined") {
      return;
    }
    const referrer = document.referrer;
    if (!referrer) {
      setIsDirectAuctionEntry(true);
      return;
    }
    try {
      const refOrigin = new URL(referrer).origin;
      setIsDirectAuctionEntry(refOrigin !== window.location.origin);
    } catch (_error) {
      setIsDirectAuctionEntry(false);
    }
  }, [eventSlug]);

  useEffect(() => {
    setActiveCarouselIndex((prev) => {
      if (carouselArtworks.length === 0) {
        return 0;
      }
      return Math.min(prev, carouselArtworks.length - 1);
    });
  }, [carouselArtworks.length]);

  const requiresAuctionPopup = useMemo(() => {
    if (!eventSlug) {
      return false;
    }
    if (eventConfig?.forceAuctionPopup) {
      return true;
    }
    return DIRECT_ENTRY_POPUP_SLUGS.has(eventSlug);
  }, [eventConfig?.forceAuctionPopup, eventSlug]);

  const shouldEnableAuctionPopup = requiresAuctionPopup && isDirectAuctionEntry;
  const { showPopup: showAuctionPopup } = useProtectActionUnregistered({
    typeOfProtection: "USER_LIMIT_VIEW",
    isEnabled: shouldEnableAuctionPopup,
    popupDelayMs: shouldEnableAuctionPopup ? 0 : undefined,
  });

  const shouldShowLoader = artworksLoading && !queryData;
  const shouldRedirectMissingEvent = !eventConfig;
  if (shouldRedirectMissingEvent) {
    return <Navigate to="/" replace />;
  }

  const pageClassName = `collection-page__page${isMilfshakes ? " auction-page__page--milfshakes" : ""}`;
  const shouldDisableCarouselArrows = !hasCarouselControls || isTouchViewport;
  const headerContent = isMilfshakes ? (
    <span className="auction-page__header-content">
      <span className="auction-page__milfshakes-brand">
        <span className="auction-page__milfshakes-logos">
          <img
            src={MilfshakesLogo}
            alt="Milfshakes Auction"
            className="auction-page__milfshakes-logo"
          />
        </span>
      </span>
      <ToggleModeDarkLight className="auction-page__theme-toggle" />
    </span>
  ) : (
    displayName
  );

  return (
    <>
      <StaticSeoHead
        title={pageTitle}
        description={metaDescription}
        canonicalPath={`/auction/${eventSlug}`}
        pageType="CollectionPage"
        breadcrumbItems={[
          { name: 'Home', url: 'https://suarte.art/' },
          { name: displayName, url: `https://suarte.art/auction/${eventSlug}` },
        ]}
      />
      {showAuctionPopup && shouldEnableAuctionPopup && (
        <AuctionUnregisteredPopup showPopUp={showAuctionPopup} />
      )}
      <AppNavigationPage>
        <div className={pageClassName}>
          <HomeHeader
            onInput={handleSearch}
            value={query.q}
            text={headerContent}
            placeholder="Search for artworks..."
            type="usual"
          />

          {shouldShowCarousel && (
            <section className="auction-page__carousel-section element-non-selectable">
              <div className="auction-page__carousel" aria-live="polite">
                <button
                  type="button"
                  className="auction-page__carousel-arrow prev"
                  aria-label="Previous artwork preview"
                  onClick={handleCarouselPrev}
                  disabled={shouldDisableCarouselArrows}
                >
                  <svg className="auction-page__carousel-arrow-icon" viewBox="0 0 24 24" role="img" aria-hidden="true">
                    <path d="M15.88 12l-7.07 7.07a1 1 0 01-1.42-1.42L12.04 12 7.39 7.35a1 1 0 011.42-1.42L15.88 12z" />
                  </svg>
                </button>

                <div
                  className="auction-page__carousel-stage"
                  id="auction-carousel-stage"
                  onTouchStart={handleCarouselTouchStart}
                  onTouchMove={handleCarouselTouchMove}
                  onTouchEnd={handleCarouselTouchEnd}
                  onTouchCancel={handleCarouselTouchEnd}
                  onWheel={handleCarouselWheel}
                >
                  {carouselArtworks.map((artwork, index) => {
                    const imageId = artwork?.artwork_media?.artwork_main_picture?.image_id;
                    const imageUrl = imageId
                      ? `${config.app.imageServiceDomain}/${imageId}/w=1200`
                      : null;
                    const isActive = index === activeCarouselIndex;

                    return (
                      <figure
                        key={artwork?._id ?? artwork?.slug ?? index}
                        className={`auction-page__carousel-card ${resolveCarouselCardState(index)}`}
                        data-index={index}
                        role={isActive ? "button" : undefined}
                        tabIndex={isActive ? 0 : -1}
                        aria-label={isActive ? buildCarouselAlt(artwork) : undefined}
                        onClick={isActive ? () => handleCarouselCardClick(artwork) : undefined}
                        onKeyDown={isActive ? (event) => handleCarouselCardKeyDown(event, artwork) : undefined}
                      >
                        {imageUrl && (
                          <img src={imageUrl} alt={buildCarouselAlt(artwork)} loading="lazy" />
                        )}
                      </figure>
                    );
                  })}
                </div>

                <button
                  type="button"
                  className="auction-page__carousel-arrow next"
                  aria-label="Next artwork preview"
                  onClick={handleCarouselNext}
                  disabled={shouldDisableCarouselArrows}
                >
                  <svg className="auction-page__carousel-arrow-icon" viewBox="0 0 24 24" role="img" aria-hidden="true">
                    <path d="M15.88 12l-7.07 7.07a1 1 0 01-1.42-1.42L12.04 12 7.39 7.35a1 1 0 011.42-1.42L15.88 12z" />
                  </svg>
                </button>
              </div>
              {activeCarouselTitle && (
                <p className="auction-page__carousel-active-title">{activeCarouselTitle}</p>
              )}
              {activeCarouselMeta.hasAuction && activeCarouselEndsAt && (
                <AuctionTimeLeftIndicator
                  endsAt={activeCarouselEndsAt}
                  className="auction-page__carousel-timer auction-time-indicator auction-time-indicator--running artwork-card__auction-timer"
                />
              )}
              {activeCarouselMeta.hasAuction && (
                <div className="auction-page__carousel-meta">
                  <div
                    className={`auction-page__carousel-winner-chip artwork-auction-desktop__bid-item${
                      activeCarouselMeta.winner ? " artwork-auction-desktop__bid-item--winner" : ""
                    }`}
                  >
                    {activeCarouselMeta.winner ? (
                      <>
                        <UserProfileImage
                          image={activeCarouselMeta.winner.image || undefined}
                          typeOfProfile={activeCarouselMeta.winner.type || undefined}
                          className="artwork-auction-desktop__bidder-avatar"
                        />
                        <div className="artwork-auction-desktop__bid-content">
                          <div className="artwork-auction-desktop__bidder">
                            <span className="artwork-auction-desktop__bidder-name">
                              {activeCarouselMeta.winner.name}
                            </span>
                          </div>
                          <span className="artwork-auction-desktop__bid-amount">
                            {activeCarouselMeta.winner.amount}
                          </span>
                        </div>
                      </>
                    ) : (
                      <div className="auction-page__carousel-winner-chip-empty">
                        <span className="auction-page__carousel-winner-chip-label">
                          Winning bid
                        </span>
                        <p>Be the first to bid on this artwork.</p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </section>
          )}

          {!isMilfshakes && (
            <div className="collection-page__sort-wrapper">
              <DropdownSort
                defaultOption={query.sort}
                options={["Relevant", "Recent", "High to low", "Low to high"]}
                onChangeOption={handleSort}
              />
            </div>
          )}

          {shouldShowLoader ? (
            <Loader />
          ) : artworks.length === 0 ? (
            <Text className="collection-page__empty-text font-size-medium" paragraph>
              No artworks available for this auction right now.
            </Text>
          ) : (
            <VirtualList
              key={queryData?.queryString ?? "auction-list"}
              items={{
                data: artworks,
                totalDocs,
              }}
              onLoadMore={loadMoreData}
              navigateToArtwork={navigateToArtwork}
              artworkPreviewSize={600}
              collectionName={displayName}
              showAuctionMeta
            />
          )}
        </div>
      </AppNavigationPage>
    </>
  );
}
